import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Interaction, InteractionData } from '@redpanda/types'; // Import InteractionData
import { createInteraction, updateInteraction } from '../services/api';
import { useInteractionStore } from '../stores/interactionStore';
import { Input, Button, Textarea, LoadingButton, FormFieldWithError } from '@redpanda/ui';
import { canEditArchivedCustomerData } from '../utils/customerUtils';

interface Customer {
  id: string;
  name: string;
  email?: string;
  company?: string;
  archived?: boolean;
}

interface InteractionFormProps {
  customerId: string;
  customer?: Customer; // 新增客戶對象
  initialData?: Interaction | null; // For editing existing interaction
  onInteractionSaved: (mode: 'create' | 'update') => void; // Callback after add/edit
  onCancelEdit?: () => void; // Optional: Callback to cancel editing
}

const InteractionForm: React.FC<InteractionFormProps> = ({ customerId, customer, initialData, onInteractionSaved, onCancelEdit }) => {
  const { t } = useTranslation();
  const { register, handleSubmit, reset, formState: { errors } } = useForm<InteractionData>({
    defaultValues: {
      type: initialData?.type || '',
      content: initialData?.content || '',
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addInteraction, updateInteraction: updateInteractionInStore } = useInteractionStore();
  
  const isEditMode = !!initialData;
  // 檢查是否可以編輯（封存客戶的關聯資料不能編輯）
  const canEdit = !isEditMode || canEditArchivedCustomerData(customer);
  

  useEffect(() => {
    if (initialData) {
      reset({ type: initialData.type, content: initialData.content });
    } else {
      reset({ type: '', content: '' }); // Clear form for new interaction
    }
  }, [initialData, reset]);

  const onSubmit = async (data: InteractionData) => {
    // 檢查是否可以編輯封存客戶的資料
    if (isEditMode && !canEdit) {
      toast.error(t('errors.business.cannotEditArchivedCustomerData', '無法編輯封存客戶的關聯資料'));
      return;
    }
    
    setIsSubmitting(true);
    const mode = initialData ? 'update' : 'create';
    try {
      const payload = { type: data.type, content: data.content };

      if (initialData) {
        const response = await updateInteraction(customerId, initialData.id, payload);
        // 更新快取中的互動
        updateInteractionInStore(customerId, response.data);
      } else {
        const response = await createInteraction(customerId, payload);
        // 新增到快取中
        addInteraction(customerId, response.data);
      }
      
      reset();
      onInteractionSaved(mode);
    } catch (err: any) {
      const errorKey = initialData ? 'components.interactions.updateError' : 'components.interactions.addError';
      toast.error(t(errorKey));
    } finally {
      setIsSubmitting(false);
    }
  };

  const formTitle = initialData ? t('components.interactions.editTitle') : t('components.interactions.addNew');

  return (
    <section className="bg-surface rounded-lg border border-line p-5">
      <h4 className="text-base font-semibold text-fg mb-3">{formTitle}</h4>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* 封存客戶警告提示 */}
        {isEditMode && !canEdit && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  {t('common.warnings.archivedCustomerData')}
                </h3>
                <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                  <p>{t('common.warnings.archivedCustomerDataDetail')}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        <FormFieldWithError
          label={t('components.interactions.type')}
          required={true}
          error={errors.type ? t('errors.validation.fieldRequired') : undefined}
          htmlFor="type"
        >
          <Input
            id="type"
            type="text"
            {...register('type', { required: true })}
            variant={errors.type ? "error" : "default"}
            disabled={!canEdit}
          />
        </FormFieldWithError>
        <FormFieldWithError
          label={t('components.interactions.content')}
          required={true}
          error={errors.content ? t('errors.validation.fieldRequired') : undefined}
          htmlFor="content"
        >
          <Textarea
            id="content"
            rows={3}
            {...register('content', { required: true })}
            disabled={!canEdit}
          />
        </FormFieldWithError>
        
        <div className="flex justify-end gap-3">
          {initialData && onCancelEdit && (
            <Button
              type="button"
              onClick={onCancelEdit}
              variant="secondary"
            >
              {t('common.actions.cancel')}
            </Button>
          )}
          <LoadingButton
            loading={isSubmitting}
            isEditMode={!!initialData}
            disabled={!canEdit}
            customText={{
              create: t('components.interactions.submitAdd'),
              createLoading: t('components.interactions.addingText'),
              update: t('components.interactions.submitEdit'),
              updateLoading: t('components.interactions.updatingText')
            }}
          />
        </div>
      </form>
    </section>
  );
};

export default InteractionForm;
